import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, ScrollText } from "lucide-react";
import type { AuditLog } from "@shared/schema";
import { format } from "date-fns";

export default function AuditLog() {
  const [page, setPage] = useState(0);
  const limit = 50;
  const offset = page * limit;

  const { data: logs = [], isLoading } = useQuery<AuditLog[]>({
    queryKey: ["/api/admin/audit-log", limit, offset],
    queryFn: async () => {
      const res = await fetch(`/api/admin/audit-log?limit=${limit}&offset=${offset}`);
      if (!res.ok) throw new Error("Failed to fetch audit logs");
      return res.json();
    },
  });

  const getActionColor = (action: string) => {
    if (action.startsWith("CREATE")) return "default";
    if (action.startsWith("UPDATE")) return "secondary";
    if (action.startsWith("DELETE")) return "destructive";
    return "outline";
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" data-testid="text-page-title">
          Audit Log
        </h1>
        <p className="text-muted-foreground mt-2">
          View system activity and administrative actions
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>Activity History</CardTitle>
          <div className="flex items-center gap-2">
            <Button aria-label="Previous page"
              variant="outline"
              size="icon"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="button-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1}
            </span>
            <Button aria-label="Next page"
              variant="outline"
              size="icon"
              onClick={() => setPage((p) => p + 1)}
              disabled={logs.length < limit}
              data-testid="button-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <div className="flex justify-center mb-3">
                <div className="rounded-full bg-muted p-3">
                  <ScrollText className="h-6 w-6 text-muted-foreground" />
                </div>
              </div>
              <p className="font-medium">No activity yet</p>
              <p className="text-sm text-muted-foreground mt-1">Audit log entries will appear here as users interact with the system.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Table</TableHead>
                  <TableHead>Target ID</TableHead>
                  <TableHead>User ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="font-mono text-sm">
                      {format(new Date(log.createdAt), "yyyy-MM-dd HH:mm:ss")}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getActionColor(log.action)}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {log.targetTable || "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground truncate max-w-[150px]">
                      {log.targetId || "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground truncate max-w-[150px]">
                      {/* D-190.6 (s190): the actor FK is ON DELETE SET NULL, so this is null once
                          the acting admin is deleted. NOT the sibling cells’ “—”: a dash means “no
                          value”, but this value existed and was removed, which is the fact the
                          cascade change exists to preserve. Ruled by Reade, s190. */}
                      {log.userId ?? "(deleted user)"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
